import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

/** Handoff info persisted when Maverick routes the user to another screen */
export interface MaverickHandoff {
  taskId: string;
  taskLabel: string;
  jobId: string;
  customerName: string;
  actionType: string;
  returnRoute: string; // always "/demo-lab" or wherever Maverick lives
  reason: string;      // why the user was sent here
  instruction: string; // what they should do
}

interface MaverickState {
  active: boolean;
  selectedTaskId: string | null;
  /** Active handoff when user is on another screen completing a task */
  handoff: MaverickHandoff | null;
  /** After completing a task, the ID of the next suggested task */
  nextSuggestedTaskId: string | null;
  /** Track completed task IDs within this session for continuity */
  completedTaskIds: string[];
}

interface MaverickContextValue {
  state: MaverickState;
  enableMaverick: () => void;
  disableMaverick: () => void;
  toggleMaverick: () => void;
  selectTask: (id: string | null) => void;
  /** Set handoff context before navigating away */
  setHandoff: (handoff: MaverickHandoff | null) => void;
  /** Clear handoff (on return) */
  clearHandoff: () => void;
  /** Mark a task as completed and optionally suggest next */
  completeTask: (taskId: string, nextTaskId?: string | null) => void;
  /** Set the next suggested task */
  suggestNext: (taskId: string | null) => void;
}

const MaverickContext = createContext<MaverickContextValue | null>(null);

export function MaverickProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MaverickState>({
    active: false,
    selectedTaskId: null,
    handoff: null,
    nextSuggestedTaskId: null,
    completedTaskIds: [],
  });

  const enableMaverick = useCallback(() =>
    setState(s => ({ ...s, active: true })), []);

  const disableMaverick = useCallback(() =>
    setState({ active: false, selectedTaskId: null, handoff: null, nextSuggestedTaskId: null, completedTaskIds: [] }), []);

  const toggleMaverick = useCallback(() =>
    setState(s => s.active
      ? { active: false, selectedTaskId: null, handoff: null, nextSuggestedTaskId: null, completedTaskIds: [] }
      : { ...s, active: true }
    ), []);

  const selectTask = useCallback((id: string | null) =>
    setState(s => ({ ...s, selectedTaskId: id, nextSuggestedTaskId: null })), []);

  const setHandoff = useCallback((handoff: MaverickHandoff | null) =>
    setState(s => ({ ...s, handoff })), []);

  const clearHandoff = useCallback(() =>
    setState(s => ({ ...s, handoff: null })), []);

  const completeTask = useCallback((taskId: string, nextTaskId?: string | null) =>
    setState(s => ({
      ...s,
      completedTaskIds: [...s.completedTaskIds, taskId],
      nextSuggestedTaskId: nextTaskId ?? null,
      selectedTaskId: nextTaskId ?? s.selectedTaskId,
    })), []);

  const suggestNext = useCallback((taskId: string | null) =>
    setState(s => ({ ...s, nextSuggestedTaskId: taskId })), []);

  return (
    <MaverickContext.Provider value={{
      state, enableMaverick, disableMaverick, toggleMaverick,
      selectTask, setHandoff, clearHandoff, completeTask, suggestNext,
    }}>
      {children}
    </MaverickContext.Provider>
  );
}

export function useMaverick() {
  const ctx = useContext(MaverickContext);
  if (!ctx) throw new Error("useMaverick must be used within MaverickProvider");
  return ctx;
}
